/**
 * Camlin Companion — desktop panel layout helpers (round 5, 6 Aug 2026).
 *
 * Two Keith items live here:
 *   - half-mode divider ratio: the visitor can drag the boundary between
 *     the website and the avatar panel; the chosen ratio persists across
 *     visits (same storage contract as consent.ts / voiceHint.ts).
 *   - host-page push: when the tall or half panel is active the page must
 *     be fully viewable in its own region, not overlaid — classic
 *     side-drawer behaviour via a margin on the host <body>.
 */

import type { CompanionConsentStorage } from './consent';

export const COMPANION_HALF_RATIO_STORAGE_KEY = 'camlin-companion-half-ratio-v1';

/** Panel fraction of the viewport width in half mode. */
export const HALF_RATIO_DEFAULT = 0.5;
export const HALF_RATIO_MIN = 0.25;
export const HALF_RATIO_MAX = 0.75;

export function clampHalfRatio(ratio: number): number {
  if (!Number.isFinite(ratio)) return HALF_RATIO_DEFAULT;
  return Math.min(HALF_RATIO_MAX, Math.max(HALF_RATIO_MIN, ratio));
}

export function readCompanionHalfRatio(storage: CompanionConsentStorage): number {
  const raw = storage.getItem(COMPANION_HALF_RATIO_STORAGE_KEY);
  if (!raw) return HALF_RATIO_DEFAULT;
  return clampHalfRatio(Number.parseFloat(raw));
}

export function writeCompanionHalfRatio(storage: CompanionConsentStorage, ratio: number): void {
  storage.setItem(COMPANION_HALF_RATIO_STORAGE_KEY, String(clampHalfRatio(ratio)));
}

/**
 * Push the host page out from under the panel by putting a right margin on
 * <body> (the classic side-drawer embed move — flow content reflows into
 * the remaining region; the panel owns the rest). Returns a restore
 * function that puts the body's inline styles back exactly as found, so
 * host pages that set their own body margins are never clobbered.
 */
export function pushHostPage(width: string): () => void {
  if (typeof document === 'undefined') {
    return () => { /* SSR no-op */ };
  }
  const body = document.body;
  const prevMargin = body.style.marginRight;
  const prevTransition = body.style.transition;
  body.style.transition = 'margin-right 0.25s ease';
  body.style.marginRight = width;
  return () => {
    body.style.marginRight = prevMargin;
    body.style.transition = prevTransition;
  };
}
